import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import NumberFlow from "@number-flow/react"
import { useEffect, useRef, useState } from "react"
import { ENTITY_META, type EntityGroupType } from "../../api/ingestionApi"
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion"
import { SectionLabel } from "../shared/SectionLabel"
import { ENTITY_ICONS, type EntityIconComponent } from "./EntityIcons"

const ENTITY_TYPES = Object.keys(ENTITY_META) as EntityGroupType[]

/** Delay between adjacent columns settling from rolling to real values. */
const SETTLE_STAGGER_MS = 150
/** Tick rate of the rolling placeholder digits. */
const ROLL_MS = 400

interface EntityScoreboardProps {
  /** Count per entity type — pass 0 for types with nothing to show. */
  counts: Record<EntityGroupType, number>
  /**
   * While true, columns show a dimmed rolling placeholder digit instead of
   * counts (split-flap effect during analysis). When it flips to false the
   * columns settle to the real values staggered left-to-right.
   */
  pending?: boolean
}

/**
 * Row of per-entity-type stat cards (icon, animated count, label). Shared
 * between the processing page ("what we found") and the post-commit summary
 * ("what we wrote") so both moments look consistent.
 */
export function EntityScoreboard({
  counts,
  pending = false,
}: EntityScoreboardProps) {
  const { translate } = useTranslation()

  // -1 = nothing settled. Only animate the stagger after an actual pending
  // phase — consumers that mount with real values (commit summary) render
  // them immediately.
  const wasPending = useRef(pending)
  const [settledThrough, setSettledThrough] = useState(
    pending ? -1 : ENTITY_TYPES.length,
  )

  useEffect(() => {
    if (pending) {
      wasPending.current = true
      setSettledThrough(-1)
      return undefined
    }
    if (!wasPending.current) return undefined
    const timeouts = ENTITY_TYPES.map((_, i) =>
      setTimeout(
        () => setSettledThrough((s) => Math.max(s, i)),
        i * SETTLE_STAGGER_MS,
      ),
    )
    return () => timeouts.forEach(clearTimeout)
  }, [pending])

  return (
    <div className="flex w-full max-w-4xl items-stretch divide-x divide-grey4">
      {ENTITY_TYPES.map((type, index) => (
        <ScoreboardStat
          key={type}
          icon={ENTITY_ICONS[type]}
          label={translate(ENTITY_META[type].labelKey)}
          value={counts[type] ?? 0}
          settled={
            index <= settledThrough || settledThrough >= ENTITY_TYPES.length
          }
          rollOffset={index}
        />
      ))}
    </div>
  )
}

function ScoreboardStat({
  icon: Icon,
  label,
  value,
  settled,
  rollOffset,
}: {
  icon: EntityIconComponent
  label: string
  value: number
  settled: boolean
  rollOffset: number
}) {
  return (
    <div className="flex min-w-4 flex-1 flex-col items-center px-6">
      <div className="rounded-full p-1">
        <Icon className="h-4 w-4 text-grey5" />
      </div>
      {settled ? (
        <NumberFlow
          value={value}
          translate="no"
          className="notranslate text-3xl font-semibold tracking-tight text-foreground transition-colors duration-300"
        />
      ) : (
        <RollingDigit offset={rollOffset} />
      )}
      <SectionLabel>{label}</SectionLabel>
    </div>
  )
}

/**
 * Dimmed single digit cycling 0-9 — reads as "still counting", never as a
 * real value. One digit only, so nobody mistakes the placeholder for a count.
 * Columns start offset from each other so the board doesn't tick in sync.
 */
function RollingDigit({ offset }: { offset: number }) {
  const [digit, setDigit] = useState((offset * 3) % 10)
  const prefersReducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    if (prefersReducedMotion) return undefined
    let intervalId: ReturnType<typeof setInterval> | null = null
    const startId = setTimeout(
      () => {
        intervalId = setInterval(() => setDigit((d) => (d + 1) % 10), ROLL_MS)
      },
      (offset * ROLL_MS) / 3,
    )
    return () => {
      clearTimeout(startId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [offset, prefersReducedMotion])

  return (
    <span
      aria-hidden="true"
      data-testid="rolling-digit"
      className="text-3xl font-semibold tracking-tight text-grey5 transition-colors duration-300"
    >
      {prefersReducedMotion ? "–" : <NumberFlow value={digit} />}
    </span>
  )
}
